import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  addGenericErrorNotification,
  addNotification,
  loadAllFiles,
} from 'loot-core/client/actions';
import { send } from 'loot-core/platform/client/fetch';

import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from '../../hooks/useNavigate';
import { AsyncStorageKey, useStorage } from '../../hooks/useStorage';
import { AnimatedLoading } from '../../icons/AnimatedLoading';
import { useResponsive } from '../../ResponsiveProvider';
import { theme } from '../../style';
import { ButtonWithLoading } from '../common/Button';
import { Card } from '../common/Card';
import { View } from '../common/View';

const StartModeScreen: React.FC = () => {
  const { getItem: getUserLocalData } = useStorage(AsyncStorageKey.USER_DATA);
  const { atLeastMediumWidth } = useResponsive();
  const { refreshAuthState } = useAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loading, setIsLoading] = useState(false);
  const [isExistingUser, setIsExistingUser] = useState(false);

  useEffect(() => {
    (async () => {
      const userData = await getUserLocalData();
      setIsExistingUser(!!userData);
    })();
  }, []);

  const modeSelectHandler = async isFreeTrial => {
    setIsLoading(true);
    if (isFreeTrial) {
      const { error } = await send('stripe-create-trial-session');
      if (error) {
        if (error.message === 'TIMEOUT') {
          addGenericErrorNotification();
        } else {
          dispatch(
            addNotification({
              type: 'error',
              message: error.message,
            }),
          );
        }
        setIsLoading(false);
        return;
      }
    }
    await refreshAuthState();
    await dispatch(loadAllFiles());
    navigate('/');
    setIsLoading(false);
  };

  return (
    <Card
      style={{
        padding: 20,
        textAlign: 'center',
        maxWidth: 856,
        minHeight: 'auto',
        position: 'relative',
        opacity: loading ? 0.9 : 1,
        marginBottom: 15,
      }}
    >
      {loading && (
        <View
          style={{
            position: 'absolute',
            top: -20,
            left: -20,
            bottom: -20,
            right: -20,
            zIndex: 11000,
            justifyContent: 'center',
          }}
        >
          <AnimatedLoading width={40} color={theme.pageTextDark} />
        </View>
      )}
      <h2>Get Started Your Way</h2>
      <span>
        We offer two hassle-free options for you to get started easily. <br />
        So you can have a good idea on whether Good Steward works for you, with
        no strings attached!
      </span>
      <View style={{ gap: 16, marginBottom: 16, marginTop: 16 }}>
        <div
          style={{
            display: 'flex',
            flexDirection: !atLeastMediumWidth ? 'column' : 'row',
            flexWrap: 'wrap',
            gap: 20,
            padding: '0 16px',
            flex: 1,
          }}
        >
          <ButtonWithLoading
            onClick={() => modeSelectHandler(false)}
            style={{
              flex: 1,
              boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
              border: 'none',
              padding: 16,
              cursor: 'pointer',
              minWidth: 185,
            }}
            childrenWrapperStyles={{
              height: '100%',
              width: '100%',
            }}
            disabled={loading}
          >
            <View
              style={{
                justifyContent: 'space-between',
                height: '100%',
                width: '100%',
                gap: 24,
                paddingTop: 12,
              }}
            >
              <div
                style={{
                  minHeight: !atLeastMediumWidth ? 'auto' : 37,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <span>
                  <strong style={{ fontSize: 18 }}>
                    Private + Free Forever
                  </strong>
                </span>
              </div>
              <ul
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  width: '100%',
                  gap: 8,
                  margin: 0,
                  flex: 1,
                  paddingLeft: 24,
                  paddingRight: 10,
                }}
              >
                <li style={{ textAlign: 'start' }}>
                  Local-only (data stays on your device) and completely private
                </li>
                <li style={{ textAlign: 'start' }}>
                  Budget builder & proactive yearly planning
                </li>
                <li style={{ textAlign: 'start' }}>
                  Manual entry or batch import of bank transactions
                </li>
                <li style={{ textAlign: 'start' }}>
                  Easily categorize & track against your budget
                </li>
                <li style={{ textAlign: 'start' }}>
                  Upgrade to premium features anytime
                </li>
                <li style={{ textAlign: 'start' }}>
                  And much more for all your budgeting needs
                </li>
              </ul>
              <span
                style={{
                  fontSize: 18,
                  fontWeight: 'bold',
                  borderTop: '1px solid rgba(0, 0, 0, 0.05)',
                  paddingTop: 24,
                  paddingBottom: 8,
                }}
              >
                {`${isExistingUser ? 'Continue using' : 'Use'} Free Version`}
              </span>
            </View>
          </ButtonWithLoading>
          <ButtonWithLoading
            onClick={() => modeSelectHandler(true)}
            style={{
              flex: 1,
              boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
              border: 'none',
              padding: 16,
              cursor: 'pointer',
              minWidth: 185,
            }}
            childrenWrapperStyles={{
              height: '100%',
              width: '100%',
            }}
            disabled={loading}
          >
            <View
              style={{
                justifyContent: 'space-between',
                height: '100%',
                width: '100%',
                gap: 24,
                paddingTop: 12,
              }}
            >
              <div
                style={{
                  minHeight: !atLeastMediumWidth ? 'auto' : 37,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <span>
                  <strong style={{ fontSize: 18 }}>
                    30-day free trial of all premium features
                  </strong>
                  <br />
                  (No CC required)
                </span>
              </div>
              <ul
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 8,
                  margin: 0,
                  flex: 1,
                  paddingLeft: 24,
                  paddingRight: 10,
                }}
              >
                <li style={{ textAlign: 'start' }}>
                  Everything from the Free Version + all our premium features
                </li>
                <li style={{ textAlign: 'start' }}>
                  Automatic data import by connecting to any of our 20k+
                  supported financial institutions
                </li>
                <li style={{ textAlign: 'start' }}>
                  Secure data backup in the cloud + Seamless data syncing across
                  all your devices
                </li>
                <li style={{ textAlign: 'start' }}>
                  AI powered onboarding with automatic categorization & budget
                  generation
                </li>
              </ul>
              <span
                style={{
                  fontSize: 18,
                  fontWeight: 'bold',
                  borderTop: '1px solid rgba(0, 0, 0, 0.05)',
                  paddingTop: 24,
                  paddingBottom: 8,
                }}
              >
                Start Free Trial
              </span>
            </View>
          </ButtonWithLoading>
        </div>
      </View>
    </Card>
  );
};
export default StartModeScreen;
